/**
 * Унифицированный компонент состояния загрузки
 * Дата: 30.01.2026
 * 
 * Используется в:
 * - Все страницы с таблицами (Users, Templates, Services, AutoReplies, etc.)
 * - Модальные окна при загрузке данных
 * - Dashboard при загрузке статистики
 * 
 * Связан с: components/ui/Spinner.tsx, components/ui/index.ts
 */
import { cn } from '@/utils/cn';
import { Spinner } from './Spinner';

export interface LoadingStateProps {
  /** Текст загрузки */
  text?: string;
  /** Размер: sm, md, lg */
  size?: 'sm' | 'md' | 'lg';
  /** Inline режим (без padding) */
  inline?: boolean;
  /** Дополнительные классы */
  className?: string;
}

/**
 * LoadingState — унифицированный компонент загрузки
 * 
 * @example
 * // Базовое использование
 * {isLoading && <LoadingState />}
 * 
 * @example
 * // С кастомным текстом
 * <LoadingState text="Загрузка пользователей..." size="lg" />
 */
export function LoadingState({
  text = 'Загрузка...',
  size = 'md',
  inline = false,
  className,
}: LoadingStateProps) {
  const sizeClasses = {
    sm: 'py-4',
    md: 'py-8',
    lg: 'py-12',
  };

  const spinnerSizes = {
    sm: 'sm' as const,
    md: 'md' as const,
    lg: 'lg' as const,
  };

  const textSizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <div 
      className={cn(
        'flex flex-col items-center justify-center text-center text-shell-muted',
        !inline && sizeClasses[size],
        className
      )}
    >
      <Spinner size={spinnerSizes[size]} />
      {text && (
        <span className={cn('mt-2', textSizes[size])}>
          {text}
        </span>
      )}
    </div>
  );
}
